import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Save, RestartAlt } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { punchSettingsApi, PunchSettingsPayload } from '../../../api/punchSettingsApi';
import { toast } from 'react-toastify';

const createDefaultState = (tenantId: string): PunchSettingsPayload => ({
  tenantId,
  isSelfieRequiredCheckIn: false,
  isSelfieRequiredCheckOut: false,
  checkoutLocationMode: 'FLEXIBLE',
  isGeofenceEnabled: false,
  isWebCheckinEnabled: false,
  webCheckinDefault: false,
  isWebSelfieCheckinRequired: false,
  isWebSelfieCheckoutRequired: false,
  isEmployeeFaceAttendanceEnabled: false,
  isSupervisorEntryEnabled: false,
  isLiveTrackingEnabled: false,
});

const PunchSettingsPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const tenantId = user?.tenantId ?? '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<PunchSettingsPayload>(createDefaultState(tenantId));
  const [initialForm, setInitialForm] = useState<PunchSettingsPayload>(createDefaultState(tenantId));

  const canEdit = useMemo(() => {
    const roles = user?.roles ?? [];
    const permissions = user?.permissions ?? [];
    const isAdminRole = roles.some((r) => r.toLowerCase() === 'admin');
    const hasManageShiftsPermission = permissions.includes('ManageShifts');
    return isAdminRole || hasManageShiftsPermission;
  }, [user?.roles, user?.permissions]);

  const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initialForm), [form, initialForm]);

  const loadSettings = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await punchSettingsApi.getByTenant(tenantId);
      setForm(result);
      setInitialForm(result);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Unable to load punch settings');
      const fallback = createDefaultState(tenantId);
      setForm(fallback);
      setInitialForm(fallback);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    setForm(createDefaultState(tenantId));
    setInitialForm(createDefaultState(tenantId));
    loadSettings();
  }, [tenantId, loadSettings]);

  const updateBool = (key: keyof PunchSettingsPayload) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: event.target.checked }));
  };

  const handleCancel = () => {
    setForm(initialForm);
  };

  const handleSave = async () => {
    if (!tenantId) {
      toast.error('Tenant not found in current session');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form, tenantId };
      await punchSettingsApi.save(payload);
      setInitialForm(payload);
      setForm(payload);
      toast.success('Punch settings saved successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save punch settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} sx={{ mb: 2, textTransform: 'none' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Punch Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure tenant-level check-in behavior for mobile, web, and live tracking.
          </Typography>
        </Box>
        
      </Stack>

      {!canEdit && (
        <Alert severity="warning" sx={{ mb: 2, textTransform: 'none' }}>
          You do not have permission to update punch settings. You can still review current tenant configuration.
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700}>1. Mobile Checkin Settings</Typography>
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={1.2}>
                <FormControlLabel
                  control={<Switch checked={form.isSelfieRequiredCheckIn} onChange={updateBool('isSelfieRequiredCheckIn')} disabled={!canEdit || saving} />}
                  label="Upload Selfie During Check-In"
                />
                <FormControlLabel
                  control={<Switch checked={form.isSelfieRequiredCheckOut} onChange={updateBool('isSelfieRequiredCheckOut')} disabled={!canEdit || saving} />}
                  label="Upload Selfie During Check-Out"
                />
                <FormControlLabel
                  control={<Switch checked={form.isGeofenceEnabled} onChange={updateBool('isGeofenceEnabled')} disabled={!canEdit || saving} />}
                  label="Enable Geofence Monitoring"
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700}>2. Web Checkin Settings</Typography>
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={1.2}>
                <FormControlLabel
                  control={<Switch checked={form.isWebCheckinEnabled} onChange={updateBool('isWebCheckinEnabled')} disabled={!canEdit || saving} />}
                  label="Enable Web Checkin"
                />
                <FormControlLabel
                  control={<Switch checked={form.webCheckinDefault} onChange={updateBool('webCheckinDefault')} disabled={!canEdit || saving} />}
                  label="Default Web Checkin Setting"
                />
                <FormControlLabel
                  control={<Switch checked={form.isWebSelfieCheckinRequired} onChange={updateBool('isWebSelfieCheckinRequired')} disabled={!canEdit || saving} />}
                  label="Upload Selfie During Web Check-In"
                />
                <FormControlLabel
                  control={<Switch checked={form.isWebSelfieCheckoutRequired} onChange={updateBool('isWebSelfieCheckoutRequired')} disabled={!canEdit || saving} />}
                  label="Upload Selfie During Web Check-Out"
                />
                <FormControlLabel
                  control={<Switch checked={form.isEmployeeFaceAttendanceEnabled} onChange={updateBool('isEmployeeFaceAttendanceEnabled')} disabled={!canEdit || saving} />}
                  label="Enable Employee Face Check-In / Check-Out"
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving || !isDirty || !canEdit}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<RestartAlt />}
          onClick={handleCancel}
          disabled={!isDirty || saving || !canEdit}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
      </Stack>
    </Box>
  );
};

export default PunchSettingsPage;
